// @flow
import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
// import {useHistory} from 'react-router-dom';
import {makeStyles} from '@material-ui/styles';
import {Hidden} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import {usePage} from '../../../page/utils';
import CustomEditor from '../components/CustomEditor/CustomEditor';
import HeaderTitle from '../components/HeaderTitle/HeaderTitle';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import LoadingSpinner from '../../../../elements/LoadingSpinner/LoadingSpinner';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import {useIsFeatureInTier} from '../../../../utils/component';
import {useTypedSelector} from '../../../../store';

import type {SaveLegalRequest} from '../api';
import type {Refunds as RefundsType} from '../duck';

type Props = {
  saveStoreRefundsConfig: (saveLegalRequest: SaveLegalRequest) => void,
  fetchStoreRefundsConfig: () => void,
  isSaving?: boolean,
  refunds?: RefundsType,
  isFetchingRefunds?: boolean,
  saveRefundsError?: string,
  saveRefundsSuccess?: boolean,
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#FFFFFF',
    '& .w-md-editor-toolbar': {
      left: '0!important',
    },
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  containerEditor: {
    position: 'relative',
    borderRadius: 8,
    border: '1px solid #E8E8ED',
    padding: theme.spacing(1.5),
    backgroundColor: colors.grey200,
    [theme.breakpoints.up('md')]: {
      padding: 0,
    },
  },
  hiddenBlock: {
    borderRadius: '8px 8px 0 0',
    backgroundColor: colors.grey200,
    height: theme.spacing(6),
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
      height: 0,
    },
  },
  buttonSave: {
    marginRight: theme.spacing(2),
    color: colors.grey900,
    height: 24,
    fontSize: 14,
    fontWeight: 600,
    border: '1px solid #D4D4DD',
    padding: theme.spacing(1, 3),
    backgroundColor: '#FFFFFF',
  },
}));
function Refunds(props: Props) {
  const {
    refunds,
    isSaving,
    isFetchingRefunds,
    saveRefundsError,
    saveRefundsSuccess,
    fetchStoreRefundsConfig,
    saveStoreRefundsConfig,
  } = props;

  const classes = useStyles();
  const legalSetting = useTypedSelector(
    state => state.legalConfig.legalSetting
  );
  const [description, setDescription] = useState('');

  const hasRefundInformationFeature = useIsFeatureInTier('STORE_POLICIES');

  const handleSaveRefunds = () => {
    if (description && description.length > 0) {
      saveStoreRefundsConfig({
        legalConfigRequest: {enabled: true, description},
      });
    }
  };

  const handleDescription = useCallback((text: string) => {
    setDescription(text);
  }, []);

  useEffect(() => {
    if (refunds && refunds.description) {
      setDescription(refunds.description);
    }
  }, [refunds]);

  useEffect(() => {
    if (hasRefundInformationFeature) {
      fetchStoreRefundsConfig();
    }
  }, [fetchStoreRefundsConfig, hasRefundInformationFeature]);

  // useEffect(() => {
  //   if (saveRefundsSuccess) history.push('/my-store/pages');
  // }, [history, saveRefundsSuccess]);

  return (
    <div className={classes.root} data-color-mode="light">
      {(isFetchingRefunds || isSaving) && <LoadingSpinner />}
      <SnackBar
        mode="error"
        message={!!saveRefundsError ? saveRefundsError : ''}
      />
      <SnackBar
        mode="success"
        message={!!saveRefundsSuccess ? 'Guardado correctamente.' : ''}
      />
      <HeaderTitle
        title="Devoluciones"
        description="Describa cómo realiza las devoluciones, cambios o reembolsos si los clientes no están satisfechos con sus pedidos. "
        enabledKeyName="refundInformationEnabled"
        enabled={legalSetting?.refundInformationEnabled}
        disabled={!hasRefundInformationFeature}
      />
      <div className={classes.containerEditor}>
        <div className={classes.hiddenBlock}>
          {hasRefundInformationFeature && (
            <Hidden smDown>
              <CustomButton
                disabled={!hasRefundInformationFeature}
                className={classes.buttonSave}
                onClick={handleSaveRefunds}
              >
                Guardar
              </CustomButton>
            </Hidden>
          )}
        </div>
        <CustomEditor
          disabled={!hasRefundInformationFeature}
          selected
          noBorder
          idx={0}
          text={description}
          onChange={handleDescription}
        />
        {hasRefundInformationFeature && (
          <Hidden mdUp>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                margin: '1rem 0 0 0',
              }}
            >
              <CustomButton
                disabled={!hasRefundInformationFeature}
                className={classes.buttonSave}
                onClick={handleSaveRefunds}
              >
                Guardar
              </CustomButton>
            </div>
          </Hidden>
        )}
      </div>
    </div>
  );
}

Refunds.propTypes = {
  saveStoreRefundsConfig: PropTypes.func.isRequired,
  fetchStoreRefundsConfig: PropTypes.func.isRequired,
  refunds: PropTypes.object,
  isSaving: PropTypes.bool,
  isFetchingRefunds: PropTypes.bool,
  saveRefundsError: PropTypes.string,
  saveRefundsSuccess: PropTypes.bool,
};

export default usePage<Props>(Refunds, 'Terms and conditions configuration');
